// Curated per-category intel for the "why it's good" section: what this
// product's clean profile avoids compared to typical conventional items.
// Keys are category slugs; 'default' applies when nothing more specific matches.

export interface IngredientIntel {
  avoid: string[];
  note: string;
}

const INTEL: Record<string, IngredientIntel> = {
  default: {
    avoid: ['Artificial colors', 'Synthetic preservatives', 'Artificial flavors'],
    note: 'Similar conventional products often rely on synthetic additives to cut costs.',
  },
  food: {
    avoid: ['High-fructose corn syrup', 'Artificial colors (Red 40, Yellow 5)', 'Sodium benzoate', 'Artificial flavors'],
    note: 'Conventional versions often lean on corn syrup, dyes, and preservatives.',
  },
  'dark-chocolate-bars': {
    avoid: ['PGPR emulsifier', 'Vanillin (artificial vanilla)', 'Palm oil', 'Excess added sugar'],
    note: 'Mass-market chocolate often swaps cocoa butter for cheaper fats and artificial vanilla.',
  },
  'peanut-butter': {
    avoid: ['Hydrogenated oils', 'Added sugar', 'Palm oil', 'Mono- and diglycerides'],
    note: 'Conventional spreads often add hydrogenated fats and sugar to peanuts that need neither.',
  },
  'muesli-and-granolas': {
    avoid: ['High-fructose corn syrup', 'BHT preservative', 'Artificial flavors'],
    note: 'Many granolas are closer to candy, sweetened heavily and preserved with BHT.',
  },
  'cereal-bars': {
    avoid: ['Corn syrup', 'Soy protein isolate', 'Artificial sweeteners', 'Palm kernel oil'],
    note: 'Typical bars use syrup binders and isolates instead of whole ingredients.',
  },
  'fruit-snacks': {
    avoid: ['Artificial colors', 'Corn syrup', 'Gelatin', 'Artificial flavors'],
    note: 'Most fruit snacks contain little real fruit and plenty of dye and syrup.',
  },
  'iced-teas': {
    avoid: ['High-fructose corn syrup', 'Caramel color', 'Sodium benzoate'],
    note: 'Bottled teas often carry more sweetener than tea.',
  },
  popcorn: {
    avoid: ['Diacetyl flavoring', 'TBHQ preservative', 'Partially hydrogenated oils'],
    note: 'Conventional microwave popcorn has a history of questionable flavoring chemicals.',
  },
  'tortilla-chips': {
    avoid: ['Artificial flavors', 'MSG', 'Low-grade frying oils'],
    note: 'Flavored chips often rely on MSG and artificial seasoning blends.',
  },
  'hot-sauces': {
    avoid: ['Xanthan-heavy fillers', 'Artificial colors', 'Sodium benzoate'],
    note: 'Quality hot sauce needs peppers, vinegar, and salt. Nothing else.',
  },
  cosmetics: {
    avoid: ['Parabens', 'Phthalates', 'Synthetic fragrance', 'Formaldehyde releasers'],
    note: 'Conventional cosmetics often use parabens and undisclosed fragrance blends.',
  },
  'sunscreens-spf-30': {
    avoid: ['Oxybenzone', 'Octinoxate', 'Retinyl palmitate', 'Synthetic fragrance'],
    note: 'Many chemical sunscreens use filters linked to irritation and reef damage.',
  },
  'sunscreens-spf-50': {
    avoid: ['Oxybenzone', 'Octinoxate', 'Retinyl palmitate', 'Synthetic fragrance'],
    note: 'Many chemical sunscreens use filters linked to irritation and reef damage.',
  },
  deodorants: {
    avoid: ['Aluminum chlorohydrate', 'Triclosan', 'Parabens', 'Synthetic fragrance'],
    note: 'Conventional antiperspirants block sweat with aluminum salts; clean deodorants neutralize odor instead.',
  },
  shampoos: {
    avoid: ['Sulfates (SLS/SLES)', 'Parabens', 'Silicones', 'Synthetic fragrance'],
    note: 'Mainstream shampoos strip with sulfates and coat with silicones.',
  },
  conditioners: {
    avoid: ['Silicones', 'Parabens', 'Synthetic fragrance', 'Isopropyl alcohol'],
    note: 'Many conditioners fake smoothness with silicone buildup.',
  },
  toothpastes: {
    avoid: ['Triclosan', 'SLS', 'Artificial sweeteners', 'Titanium dioxide'],
    note: 'Conventional toothpaste can include antibacterial agents and foaming agents you don’t need.',
  },
  'body-washes': {
    avoid: ['Sulfates', 'Parabens', 'Synthetic fragrance', 'Dyes'],
    note: 'Typical body washes foam with sulfates and scent with synthetic fragrance.',
  },
  'lip-balms': {
    avoid: ['Petrolatum', 'Parabens', 'Synthetic fragrance', 'Mineral oil'],
    note: 'Many balms coat lips with petroleum derivatives rather than nourishing them.',
  },
  'eco-friendly-home': {
    avoid: ['Chlorine bleach', 'Ammonia', 'Synthetic fragrance', 'Phosphates'],
    note: 'Conventional home products often rely on harsh chemicals that linger in your space.',
  },
  'natural-supplements': {
    avoid: ['Magnesium stearate fillers', 'Artificial colors', 'Titanium dioxide', 'Proprietary blends'],
    note: 'Cheap supplements pad capsules with fillers and hide doses in proprietary blends.',
  },
};

export const getIngredientIntel = (categorySlug?: string): IngredientIntel => {
  if (!categorySlug) return INTEL.default;
  return INTEL[categorySlug] || INTEL.default;
};
